import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

// Building and unit photo galleries (backend migration 075).
//
// Uses the DEFAULT axios instance so these authenticated calls inherit the
// interceptors in axios-setup.ts (credentials, CSRF header, 401 -> refresh ->
// retry), like units-client.ts and hierarchy-client.ts and for the same reason.

export type GalleryEntityType = 'building' | 'unit';

export interface GalleryImage {
  id: string;
  entity_type: GalleryEntityType;
  building_id: number;
  unit_id: number | null;
  content_type: string;
  bytes: number | null;
  width: number | null;
  height: number | null;
  sort_order: number;
  is_primary: boolean;
  created_at: string;
  // PRESIGNED AND SHORT-LIVED. The bucket blocks public access, so this URL
  // carries a signature that expires — it is not a stable address for the
  // image and must never be persisted anywhere (a saved copy 403s later).
  url: string;
  // Seconds the signature remains valid. `useGallery` refetches on this
  // schedule so a screen left open overnight does not decay into a grid of
  // broken thumbnails.
  expires_in: number;
}

// The server caps ONE REQUEST, not the gallery. A building may hold any number
// of photos; they just arrive in batches of at most this many.
export const MAX_FILES_PER_UPLOAD = 20;

const base = (type: GalleryEntityType, id: number) =>
  `${apiRoute}/dashboard/images/${type}/${id}`;

export const listImages = async (
  type: GalleryEntityType,
  id: number
): Promise<GalleryImage[]> => {
  const { data } = await axios.get<{ images: GalleryImage[] }>(base(type, id));

  return data.images ?? [];
};

// `files` are already-cropped blobs from the crop modal, not the originals the
// user picked. Cropping client-side means the bytes that leave the browser are
// the bytes we intend to store — and, incidentally, that EXIF (including GPS
// coordinates on a phone photo of a building) never reaches the server, because
// a canvas re-encode drops it.
export const uploadImages = async (
  type: GalleryEntityType,
  id: number,
  files: Blob[],
  onProgress?: (percent: number) => void
): Promise<GalleryImage[]> => {
  const form = new FormData();
  files.forEach((file, i) => {
    // The field name must be `images` — the server's multer instance accepts
    // that field and rejects anything else with LIMIT_UNEXPECTED_FILE.
    //
    // The extension follows the blob's ACTUAL type. The crop modal falls back
    // to JPEG where a browser cannot encode WebP, and hard-coding `.webp` here
    // would make multer's declared-type filter see a mismatch — a rejection
    // caused entirely by our own filename.
    const ext = file.type === 'image/jpeg' ? 'jpg' : 'webp';
    form.append('images', file, `photo-${i + 1}.${ext}`);
  });

  const { data } = await axios.post<{ images: GalleryImage[] }>(
    base(type, id),
    form,
    {
      // Content-Type is deliberately NOT set. The browser has to add the
      // multipart boundary itself, and naming the header strips it — the
      // request then arrives as a body multer cannot parse.
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    }
  );

  return data.images ?? [];
};

// The FULL ordered list of ids, not a set of moved-item deltas. Two people
// dragging at once with deltas produce an order neither of them chose; a whole
// list makes last-write-wins explicit. The server rejects the call if any id
// does not belong to this gallery.
export const reorderImages = async (
  type: GalleryEntityType,
  id: number,
  ids: string[]
): Promise<GalleryImage[]> => {
  const { data } = await axios.patch<{ images: GalleryImage[] }>(
    `${base(type, id)}/order`,
    { ids }
  );

  return data.images ?? [];
};

export const setPrimaryImage = async (
  type: GalleryEntityType,
  id: number,
  imageId: string
): Promise<GalleryImage> => {
  const { data } = await axios.patch<{ image: GalleryImage }>(
    `${base(type, id)}/primary/${imageId}`
  );

  return data.image;
};

export const deleteImage = async (
  type: GalleryEntityType,
  id: number,
  imageId: string
): Promise<void> => {
  await axios.delete(`${base(type, id)}/${imageId}`);
};

// One place that turns a failure into a sentence a person can act on.
//
// The server already writes a specific message for every multer rejection —
// too large, too many, wrong field, wrong type — so its `message` is preferred
// over anything invented here. The branches below cover what the server never
// gets to say: a request that died in the browser or in the proxy, where
// `error.response` is undefined and the only honest thing is to name the
// network. A bare `fallback` for all of those is how "it just doesn't work"
// bug reports get written.
export const readImageError = (error: unknown, fallback: string): string => {
  // Not an axios error at all — a canvas/encode failure thrown by the crop
  // modal, for instance. Its own message is already user-facing.
  if (error instanceof Error && !axios.isAxiosError(error)) {
    return error.message || fallback;
  }

  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: string } | undefined)
      ?.message;
    if (message) return message;

    const status = error.response?.status;
    if (status === 503) {
      return 'Photo storage is not configured on this server yet.';
    }
    if (status === 429) {
      return 'Too many uploads just now. Please wait a few minutes.';
    }
    if (status === 413) {
      // The PROXY refused the body before the app saw it. Caddy and nginx both
      // cap request size independently of multer, so this can fire even when the
      // file is inside our own limit — and the response is HTML, with no
      // `message` for the branch above to find.
      return 'That upload was too large for the server to accept. Try fewer photos at once.';
    }
    if (status === 403) {
      return 'You do not have permission to change these photos.';
    }
    if (error.code === 'ECONNABORTED') {
      return 'The upload timed out. Check your connection and try again.';
    }
    if (!error.response) {
      return 'Could not reach the server. Check your connection and try again.';
    }
  }

  return fallback;
};
